/**
 * Finding text in the rendered PDF — the core shared by the viewer's finder
 * and by "click a field, see it in the document".
 *
 * WHAT WE HAVE TO WORK WITH. pdf.js gives each page as an ordered list of text
 * ITEMS (the spans LibreOffice's PDF exposes, typically a run of same-styled
 * text), and react-pdf's `customTextRenderer` lets us rewrite each item as it
 * renders. So a match is addressed as (page, item, occurrence-within-item),
 * and highlighting is "wrap that occurrence in <mark>". A needle that spans
 * TWO items cannot be marked by this mechanism; see `findMatches`.
 *
 * MATCHING IS TOLERANT ON PURPOSE. The DOCX text and the PDF text layer rarely
 * agree byte for byte — hyphenation, ligatures, non-breaking spaces, collapsed
 * whitespace — so matching is case-insensitive and treats any run of
 * whitespace as equivalent. This is the same reasoning `locate-anchor.ts`
 * applies to guidance anchors; it is kept separate because that module
 * normalises AWAY punctuation and matches a word prefix, which is right for
 * "find the paragraph" and wrong for "find this exact value".
 */

export interface TextMatch {
  /** 1-based page number. */
  page: number;
  /** Index into that page's text items. */
  itemIndex: number;
  /** Which occurrence within that item (0-based) — an item can contain the needle twice. */
  occurrence: number;
}

/**
 * What the canvas is currently highlighting. `current` is the one match to
 * scroll to and distinguish; every other occurrence of `needle` on a rendered
 * page is marked too. `nonce` lets the same request fire twice in a row.
 */
export interface DocumentHighlight {
  needle: string;
  current: TextMatch | null;
  nonce: number;
}

/**
 * A regex that finds `needle` case-insensitively, with whitespace-run
 * tolerance.
 *
 * NOT global. A `g` regex is stateful — `test()` advances `lastIndex`, so the
 * second call on a fresh string can silently fail — and that is a footgun for
 * any caller using this as a predicate. The scanning helpers below build their
 * own global variant from `.source`.
 */
export function buildMatcher(needle: string): RegExp | null {
  const trimmed = needle.trim();
  if (!trimmed) return null;
  const escaped = trimmed
    .split(/\s+/)
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('\\s+');
  return new RegExp(escaped, 'i');
}

/** The scanning form: global, for exec loops. Fresh per call, so never shared state. */
function buildScanner(needle: string): RegExp | null {
  const m = buildMatcher(needle);
  return m ? new RegExp(m.source, 'gi') : null;
}

/**
 * Every occurrence of `needle` across all pages, in document order.
 *
 * Returns nothing for a needle that only exists across an item boundary. That
 * is a real limitation, and a bounded one: field values are short and almost
 * always sit inside one run, and section headings are single items. Long
 * multi-line needles are not what this is for — a caller wanting to locate a
 * paragraph should use `locate-anchor.ts`.
 */
export function findMatches(pageItems: string[][], needle: string): TextMatch[] {
  const matcher = buildScanner(needle);
  if (!matcher) return [];
  const out: TextMatch[] = [];
  pageItems.forEach((items, pageIdx) => {
    items.forEach((item, itemIndex) => {
      let occurrence = 0;
      matcher.lastIndex = 0;
      while (matcher.exec(item) !== null) {
        out.push({ page: pageIdx + 1, itemIndex, occurrence });
        occurrence++;
        // A zero-width match would loop forever; the escaped needle is never
        // empty, but guard the regex contract anyway.
        if (matcher.lastIndex === 0) break;
      }
    });
  });
  return out;
}

/**
 * Wrap occurrences of `needle` in `str` for react-pdf's `customTextRenderer`,
 * which takes an HTML STRING (it sets innerHTML on the item span). The text is
 * escaped first — an item is document content, and document content can
 * contain `<`.
 *
 * `currentOccurrence`, when given, marks that one occurrence with an extra
 * class so the finder's "3 of 12" can be told apart from the other eleven.
 */
export function markOccurrences(
  str: string,
  needle: string,
  currentOccurrence: number | null,
): string {
  const matcher = buildScanner(needle);
  const escaped = escapeHtml(str);
  if (!matcher) return escaped;
  // Escaping changes lengths, so match on the ORIGINAL and rebuild from the
  // escaped pieces: each piece is escaped independently and reassembled with
  // the marks in between.
  let out = '';
  let last = 0;
  let occurrence = 0;
  matcher.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = matcher.exec(str)) !== null) {
    const cls = occurrence === currentOccurrence ? 'cl-doc-mark cl-doc-mark--current' : 'cl-doc-mark';
    out += escapeHtml(str.slice(last, m.index));
    out += `<mark class="${cls}">${escapeHtml(m[0])}</mark>`;
    last = m.index + m[0].length;
    occurrence++;
    if (matcher.lastIndex === 0) break;
  }
  if (occurrence === 0) return escaped;
  out += escapeHtml(str.slice(last));
  return out;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
